import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Topbar } from './Topbar';
import { Sidebar } from './Sidebar';
import { ViewingAsBar } from './ViewingAsBar';
import { useEntityScopeReset } from './useEntityScopeReset';
import { useIsAuthenticated, useCurrentUser } from '@hooks/auth';

import type { JSX } from "react";

/**
 * Authenticated shell. Redirects to /signin when the user has no session,
 * preserving the requested URL in router state so we can bounce them back
 * after login.
 *
 * `<main>` itself has zero padding so the `<ViewingAsBar>` can render
 * flush at the top of the scroll area (pinned right under the topbar's
 * quick-search). Per-page padding lives on the inner wrapper.
 */
export function AppLayout(): JSX.Element {
  const isAuthenticated = useIsAuthenticated();
  const user = useCurrentUser();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Clear the per-segment entity scope when navigation leaves its owning
  // segment (legacy clear-on-unmount parity). Safe to call before the auth
  // guard returns — it no-ops when no scoped override is set.
  useEntityScopeReset();

  // Scroll-to-top on navigation (legacy `ScrollToTop` parity). `<main>` is the
  // scroll container here, so reset its scrollTop — scrolling `window` is a
  // no-op in this layout.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  return (
    <div className="flex h-screen flex-col">
      <Topbar />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        {/* overflow-anchor none: Chrome's scroll anchoring intermittently
            snaps this scroller to the top when a list page swaps its rows
            while sticky elements (orders toolbar/headers) live inside the
            scroller. Pages replace content wholesale, so anchoring has
            nothing to preserve here — turn it off and let scroll clamp. */}
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto [overflow-anchor:none]"
          data-testid="main-content"
        >
          <ViewingAsBar />
          <div className="px-8 pt-8 pb-16">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
